import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Mail, 
  Calendar, 
  Database, 
  MessageSquare, 
  FileText, 
  Zap,
  ArrowRight 
} from 'lucide-react';

interface Template {
  id: string;
  title: string;
  description: string;
  prompt: string;
  tags: string[];
  icon: React.ComponentType<{ className?: string }>;
  category: 'email' | 'social' | 'data' | 'scheduling' | 'content';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

interface WorkflowTemplatesProps {
  onSelectTemplate: (prompt: string) => void;
}

const templates: Template[] = [
  {
    id: 'email-customer-support',
    title: 'Email Customer Support',
    description: 'Automatically process customer emails, categorize them, and route to appropriate team members',
    prompt: 'When I receive an email to support@company.com, analyze the content to categorize it (technical, billing, feature request), extract key information, create a ticket in our system, and notify the appropriate team member via Slack',
    tags: ['Email', 'Customer Support', 'Slack'],
    icon: Mail,
    category: 'email',
    difficulty: 'intermediate'
  },
  {
    id: 'social-media-scheduler',
    title: 'Social Media Scheduler',
    description: 'Schedule and publish content across multiple social media platforms automatically',
    prompt: 'Every day at 9 AM, fetch scheduled posts from our Google Sheet, format them appropriately for each platform (Twitter, LinkedIn, Facebook), and publish them with appropriate hashtags and timing',
    tags: ['Social Media', 'Google Sheets', 'Scheduling'],
    icon: Calendar,
    category: 'social',
    difficulty: 'advanced'
  },
  {
    id: 'data-backup-sync',
    title: 'Data Backup & Sync',
    description: 'Automatically backup and sync data between different systems and cloud storage',
    prompt: 'Every week, export data from our CRM system, clean and format it, upload to Google Drive for backup, and send a summary report to the team via email',
    tags: ['Data Backup', 'CRM', 'Google Drive'],
    icon: Database,
    category: 'data',
    difficulty: 'intermediate'
  },
  {
    id: 'slack-standup',
    title: 'Daily Standup Reminder',
    description: 'Send daily standup reminders and collect team updates automatically',
    prompt: 'Every weekday at 9 AM, send a Slack message to the team channel asking for standup updates, collect responses, and compile them into a summary document',
    tags: ['Slack', 'Team Management', 'Daily Routine'],
    icon: MessageSquare,
    category: 'social',
    difficulty: 'beginner'
  },
  {
    id: 'content-aggregator',
    title: 'Content Aggregator',
    description: 'Monitor RSS feeds and automatically curate and share relevant content',
    prompt: 'Monitor 5 industry RSS feeds, use AI to summarize new articles, filter for relevance to our business, and post the best ones to our company Discord channel with summaries',
    tags: ['RSS', 'AI Summary', 'Discord'],
    icon: FileText,
    category: 'content',
    difficulty: 'advanced'
  },
  {
    id: 'webhook-processor',
    title: 'Webhook Data Processor',
    description: 'Process incoming webhook data and trigger appropriate actions',
    prompt: 'When our webhook receives new user registration data, validate the information, save it to our database, send a welcome email, and add the user to our mailing list',
    tags: ['Webhook', 'User Registration', 'Email'],
    icon: Zap,
    category: 'data',
    difficulty: 'intermediate'
  }
];

const WorkflowTemplates = ({ onSelectTemplate }: WorkflowTemplatesProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'email', label: 'Email' },
    { value: 'social', label: 'Social' },
    { value: 'data', label: 'Data' },
    { value: 'scheduling', label: 'Scheduling' },
    { value: 'content', label: 'Content' }
  ];

  const difficulties = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const filteredTemplates = templates.filter(template => {
    const categoryMatch = selectedCategory === 'all' || template.category === selectedCategory;
    const difficultyMatch = selectedDifficulty === 'all' || template.difficulty === selectedDifficulty;
    return categoryMatch && difficultyMatch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Automation Templates</h2>
        <p className="text-muted-foreground">
          Start with proven automation patterns and customize them for your needs
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 justify-center">
        <div className="flex gap-2">
          {categories.map((category) => (
            <Button
              key={category.value}
              variant={selectedCategory === category.value ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category.value)}
            >
              {category.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          {difficulties.map((difficulty) => (
            <Button
              key={difficulty.value}
              variant={selectedDifficulty === difficulty.value ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedDifficulty(difficulty.value)}
            >
              {difficulty.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => {
          const IconComponent = template.icon;
          return (
            <Card key={template.id} className="h-full hover:shadow-lg transition-all duration-200 group">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-n8n-100 dark:bg-n8n-900 rounded-lg">
                    <IconComponent className="w-5 h-5 text-n8n-600 dark:text-n8n-400" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{template.title}</CardTitle>
                    <Badge className={`text-xs ${getDifficultyColor(template.difficulty)}`}>
                      {template.difficulty}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription className="text-sm leading-relaxed">
                  {template.description}
                </CardDescription>
                
                <div className="flex flex-wrap gap-1">
                  {template.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <Button
                  onClick={() => onSelectTemplate(template.prompt)}
                  className="w-full group-hover:bg-n8n-600 transition-colors"
                  variant="outline"
                >
                  Use This Template
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            No templates found for the selected filters. Try adjusting your search criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default WorkflowTemplates;